import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CheckCircle, Loader2, Users, Rocket } from "lucide-react";
import { INDUSTRIES } from "@/lib/industries";
import { motion } from "framer-motion";

const BetaTesterCTA = () => {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company_name: "",
    phone: "",
    industry: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim() || !form.industry) return;

    setSubmitting(true);
    const { error } = await supabase.from("beta_interest_requests" as any).insert({
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      company_name: form.company_name.trim() || null,
      phone: form.phone.trim() || null,
      industry: form.industry,
      message: form.message.trim() || null,
    } as any);

    if (error) {
      if (error.code === "23505") {
        toast.info("You've already submitted a request for this industry!");
        setSubmitted(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } else {
      toast.success("Request submitted! We'll be in touch soon.");
      setSubmitted(true);
    }
    setSubmitting(false);
  };

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <section className="py-20 md:py-28 border-t border-border/50 bg-primary/[0.03]">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-6">
            <Rocket className="h-4 w-4" />
            Early Access Program
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Help Us Build the{" "}
            <span className="text-gradient">Future</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-2">
            We're looking for companies to help test our live platform and shape the industries currently in development and coming soon.
          </p>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            It's <span className="font-semibold text-foreground">completely free</span> while you help us test and refine the platform. When your industry officially launches, early testers receive <span className="font-semibold text-foreground">exclusive special pricing</span> as a thank you for your feedback.
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center gap-3 p-8 rounded-xl border border-success/30 bg-success/5 max-w-md mx-auto"
          >
            <CheckCircle className="h-10 w-10 text-success" />
            <p className="text-foreground font-semibold text-lg">We've got your request!</p>
            <p className="text-sm text-muted-foreground text-center">
              A member of our team will reach out with more information about the program soon.
            </p>
          </motion.div>
        ) : !showForm ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="flex justify-center"
          >
            <Button
              size="lg"
              onClick={() => setShowForm(true)}
              className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90 px-8 text-base h-12"
            >
              <Users className="mr-2 h-5 w-5" />
              Request More Information
            </Button>
          </motion.div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit}
            className="max-w-lg mx-auto p-6 rounded-xl border border-border bg-card space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Name *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your name"
                  className="bg-secondary border-border text-foreground mt-1"
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Email *</Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@company.com"
                  className="bg-secondary border-border text-foreground mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Company Name (optional)</Label>
                <Input
                  value={form.company_name}
                  onChange={(e) => update("company_name", e.target.value)}
                  placeholder="Company name"
                  className="bg-secondary border-border text-foreground mt-1"
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Phone (optional)</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="(555) 123-4567"
                  className="bg-secondary border-border text-foreground mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">Industry of Interest *</Label>
              <Select value={form.industry} onValueChange={(v) => update("industry", v)} required>
                <SelectTrigger className="bg-secondary border-border text-foreground mt-1">
                  <SelectValue placeholder="Select an industry" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {INDUSTRIES.map((ind) => (
                    <SelectItem key={ind.key} value={ind.key}>
                      <span className="flex items-center gap-2">
                        {ind.name}
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          ind.status === "live" ? "bg-success/20 text-success" :
                          ind.status === "development" ? "bg-info/20 text-info" :
                          "bg-muted-foreground/20 text-muted-foreground"
                        }`}>
                          {ind.status === "live" ? "Live" : ind.status === "development" ? "In Development" : "Coming Soon"}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-muted-foreground text-xs">Message (optional)</Label>
              <Textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us about your company or what you're looking for..."
                className="bg-secondary border-border text-foreground mt-1 min-h-[80px]"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Request
            </Button>
          </motion.form>
        )}
      </div>
    </section>
  );
};

export default BetaTesterCTA;
